import { useState } from 'react'

import { usePermissions } from 'commons/customHooks'
import i18n from 'commons/libs/i18n'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { getUser } from 'commons/utils/user'
import { Input, SectionCard, Select, SlimButton } from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { SampleOrderCostElement, sampleOrderCostElementOptions } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { oVMaintenance } from 'services/manage_ov'
import type { OvMaintenanceResponse } from 'services/manage_ov/interface'
import { asyncGetOVById } from 'store/OVContext'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

export const CostElementSection = () => {
  const dispatch = useDispatch()
  const { ovNumber } = useParams()
  const [loading, setLoading] = useState(false)
  const {
    ovSelected: { Cabecalho },
    detailedInfo: { FaturamentoEntrega },
  } = useSelector((state) => state.ov)

  const defaultValues = {
    CodigoCentroCusto: Cabecalho.CodigoCentroCusto,
    CodigoElementoPep: Cabecalho.CodigoElementoPep,
    OpcaoElementoCusto:
      (Cabecalho.CodigoElementoPep && SampleOrderCostElement.PEP) ||
      (Cabecalho.CodigoCentroCusto && SampleOrderCostElement.COST),
  }

  const {
    control,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  })

  const resetForm = () => {
    reset(defaultValues)
  }

  const costElementOption = watch('OpcaoElementoCusto')
  const pepElementValue = watch('CodigoElementoPep')
  const costCenterValue = watch('CodigoCentroCusto')

  const { verifyPermission } = usePermissions()

  const shouldDisableField = (action: ActionPerm) => {
    return loading || +Cabecalho.CodigoStatus !== 0 || !verifyPermission(SCREENS.OV, action)
  }

  const shouldDisableSave = () => {
    return (
      (costElementOption === SampleOrderCostElement.PEP && !pepElementValue) ||
      (costElementOption === SampleOrderCostElement.COST && !costCenterValue)
    )
  }

  const handleEdit = async (data: typeof defaultValues) => {
    setLoading(true)
    const requestBody = {
      CodigoCentroCusto: data.CodigoCentroCusto,
      CodigoElementoPep: data.CodigoElementoPep,
      CodigoEntrega: Cabecalho.CodigoSapCliente,
      CodigoUsuarioAlteracao: getUser(),
      CondicaoPagamento: Cabecalho.CondicaoPagamento,
      DataDesejadaCliente: FaturamentoEntrega.DataDesejadaCliente,
      DataPrevistaFaturamento: FaturamentoEntrega.DataPrevisaoFaturamento,
      DiasAdicionais: Cabecalho.PrazoExtra,
      NumeroOrdemVenda: `${ovNumber}`,
      NumeroPedidoCliente: Cabecalho.NumeroPedidoCliente,
      TipoOrdem: Cabecalho.CodigoTipoOrdem,
    }

    try {
      const response: OvMaintenanceResponse = await oVMaintenance(`${ovNumber}`, requestBody)
      if (response.OperacaoRealizada) {
        dispatch(asyncGetOVById(`${ovNumber}`))
        toast.success('Ordem de Venda editada com sucesso.')
      } else {
        resetForm()
        toast.error(response?.Mensagem || `Não foi possível editar a Ordem de Venda.`)
      }
    } catch (error) {
      resetForm()
      toast.error(`Erro ao editar a Ordem de Venda.`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <SectionCard
      title="Custo"
      tooltipText="Nesta seção, você pode consultar e/ou alterar o Elemento de Custo, bem como o campo dependente — Elemento PEP ou Centro de Custo — de acordo com a necessidade da OV."
    >
      <S.Wrapper onSubmit={handleSubmit(handleEdit)}>
        <Controller
          control={control}
          name="OpcaoElementoCusto"
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Select
              disabled={shouldDisableField(ACTIONS_SCREENS.SAVE)}
              error={error?.message}
              label={i18n.t('components.orderHeader.costElement')}
              menuText={i18n.t('common.placeholders.clickToSelect')}
              name={name}
              onChange={(value) => {
                setValue('CodigoCentroCusto', '')
                setValue('CodigoElementoPep', '')
                onChange(value)
              }}
              options={sampleOrderCostElementOptions}
              value={value ?? ''}
            />
          )}
        />
        {costElementOption === SampleOrderCostElement.COST && (
          <Controller
            control={control}
            name="CodigoCentroCusto"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <div>
                <Input
                  disabled={shouldDisableField(ACTIONS_SCREENS.SAVE)}
                  error={error?.message}
                  id="cd_cent_cust"
                  label={i18n.t('components.orderHeader.costCenter')}
                  name={name}
                  onChange={onChange}
                  placeholder={i18n.t('common.placeholders.clickToWrite')}
                  type="text"
                  value={value as string}
                />
              </div>
            )}
          />
        )}
        {costElementOption === SampleOrderCostElement.PEP && (
          <Controller
            control={control}
            name="CodigoElementoPep"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <div>
                <Input
                  disabled={shouldDisableField(ACTIONS_SCREENS.SAVE)}
                  error={error?.message}
                  id="cd_elem_pep"
                  label={i18n.t('components.orderHeader.pepElement')}
                  name={name}
                  onChange={onChange}
                  placeholder={i18n.t('common.placeholders.clickToWrite')}
                  type="text"
                  value={value as string}
                />
              </div>
            )}
          />
        )}
        {isDirty && (
          <S.ActionsRow>
            <SlimButton
              content="Salvar"
              disabled={shouldDisableSave()}
              loading={loading}
              type="submit"
              variant="primary"
            />
            <SlimButton
              content="Desfazer"
              disabled={loading}
              onClick={resetForm}
              type="button"
              variant="secondary"
            />
          </S.ActionsRow>
        )}
      </S.Wrapper>
    </SectionCard>
  )
}
